import {
  Crosshair,
  Shield,
  Cloud,
  Layers,
  AlertTriangle,
  Check,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/ui/lib/utils";
import type {
  PipelineGroup,
  PipelineGroupKey,
  QualityGateResult,
} from "../../utils/pipeline-groups";

/* ──────────────────────────────────────────────────────────────────
 * PipelineGroupSection — visual frame for one pipeline group.
 *
 * Accents:
 *   core          cyan, Crosshair, "always recorded"
 *   quality_gate  green/amber by live gate status, Shield, ✓ pass / ✗ blocked
 *   environment   neutral/muted, Cloud, "context · non-blocking"
 *
 * `variant="overlay"` swaps theme tokens for explicit zinc classes so
 * the section stays readable on the carousel's dark backdrop.
 * ────────────────────────────────────────────────────────────────── */

/** The group titles and blurbs live in the overlay map in English; they are
 * translated here, keyed by the same group key settings uses. */
type GroupTitleKey =
  | "pipelineGroups.titles.core"
  | "pipelineGroups.titles.quality_gate"
  | "pipelineGroups.titles.environment"
  | "pipelineGroups.titles.ungrouped";

type GroupDescriptionKey =
  | "pipelineGroups.descriptions.core"
  | "pipelineGroups.descriptions.quality_gate"
  | "pipelineGroups.descriptions.environment"
  | "pipelineGroups.descriptions.ungrouped";

const GROUP_TITLE_KEYS: Record<PipelineGroupKey, GroupTitleKey> = {
  core: "pipelineGroups.titles.core",
  quality_gate: "pipelineGroups.titles.quality_gate",
  environment: "pipelineGroups.titles.environment",
  ungrouped: "pipelineGroups.titles.ungrouped",
};

const GROUP_DESCRIPTION_KEYS: Record<PipelineGroupKey, GroupDescriptionKey> = {
  core: "pipelineGroups.descriptions.core",
  quality_gate: "pipelineGroups.descriptions.quality_gate",
  environment: "pipelineGroups.descriptions.environment",
  ungrouped: "pipelineGroups.descriptions.ungrouped",
};

const GROUP_ICONS: Record<
  PipelineGroupKey,
  React.ComponentType<{ className?: string }>
> = {
  core: Crosshair,
  quality_gate: Shield,
  environment: Cloud,
  ungrouped: Layers,
};

interface PipelineGroupSectionProps {
  group: PipelineGroup;
  /** Live quality-gate result — only meaningful for the gate group. */
  gate?: QualityGateResult | null;
  variant?: "panel" | "overlay";
  children: React.ReactNode;
}

const PipelineGroupSection = ({
  group,
  gate,
  variant = "panel",
  children,
}: PipelineGroupSectionProps) => {
  const { t } = useTranslation("mlExports");
  const overlay = variant === "overlay";
  const Icon = GROUP_ICONS[group.key];

  const accentBorder =
    group.key === "core"
      ? "border-l-cyan-500"
      : group.key === "quality_gate"
        ? gate?.status === "pass"
          ? "border-l-emerald-500"
          : gate?.status === "blocked"
            ? "border-l-amber-500"
            : overlay
              ? "border-l-zinc-700"
              : "border-l-zinc-500/40"
        : overlay
          ? "border-l-zinc-700"
          : "border-l-zinc-500/40";

  const accentIcon =
    group.key === "core"
      ? "text-cyan-500"
      : group.key === "quality_gate"
        ? gate?.status === "pass"
          ? "text-emerald-500"
          : gate?.status === "blocked"
            ? "text-amber-500"
            : overlay
              ? "text-zinc-500"
              : "text-muted-foreground"
        : overlay
          ? "text-zinc-500"
          : "text-muted-foreground";

  return (
    <section className={cn("border-l-2 pl-3", accentBorder)}>
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 min-w-0">
          <Icon className={cn("h-3.5 w-3.5 shrink-0", accentIcon)} />
          <h4
            className={cn(
              "text-[11px] font-bold uppercase tracking-widest truncate",
              overlay ? "text-zinc-400" : "text-muted-foreground",
            )}
          >
            {t(GROUP_TITLE_KEYS[group.key])}
          </h4>
        </span>

        {group.key === "quality_gate" && gate && (
          <span
            className={cn(
              "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border shrink-0",
              gate.status === "pass" &&
                "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
              gate.status === "blocked" &&
                "text-amber-600 dark:text-amber-400 bg-amber-500/10 border-amber-500/30",
              gate.status === "unknown" &&
                (overlay
                  ? "text-zinc-500 bg-zinc-800/60 border-zinc-700"
                  : "text-muted-foreground bg-muted border-border"),
            )}
          >
            {gate.status === "pass" && <Check className="h-3 w-3" />}
            {gate.status === "blocked" && <X className="h-3 w-3" />}
            {gate.status === "unknown"
              ? t("qualityGate.unknown")
              : gate.status === "pass"
                ? t("qualityGate.pass")
                : t("qualityGate.blocked")}
          </span>
        )}
      </div>

      <p
        className={cn(
          "text-[10px] mt-0.5 mb-2",
          overlay ? "text-zinc-600" : "text-muted-foreground/70",
        )}
      >
        {t(GROUP_DESCRIPTION_KEYS[group.key])}
      </p>

      <div className="space-y-2">{children}</div>

      {group.key === "quality_gate" && gate?.status === "blocked" && (
        <p className="mt-2 flex items-center gap-1 text-[10px] font-medium text-amber-600 dark:text-amber-400">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          {t("pipelineGroups.segmentationSkipped")}
        </p>
      )}
    </section>
  );
};

export default PipelineGroupSection;
